import { clearOtherAdvancedStyles } from "@blocks/utils";
import { Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React, { useEffect, useMemo } from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import LayoutOptionsRenderer from "../../../components/common/LayoutOptionsRenderer";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	AlignCenter,
	AlignLeft,
	AlignRight,
	AlignSpaceBetween,
	HeadingLayout1Style1,
	HeadingLayout2Style1,
	HeadingLayout3Style1,
	HeadingLayout4Style1,
	HeadingLayout5Style1,
	HeadingLayout6Style1,
	HeadingLayout7Style1,
	HeadingLayout8Style1,
	HeadingLayout9Style1,
	LayoutAlignBottom,
	LayoutAlignTop,
	TextAlignCenter,
	TextAlignLeft,
	TextAlignRight,
} from "../../../components/icons/Icons";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	Dimensions,
	IconPicker,
	Input,
	PopoverDrawer,
	Select,
	Slider,
	Toggle,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	UrlInput,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	tags?: any[];
	authorOptions?: any[];
	postTypeOptions?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			alignment,
			enableAutoPlay,
			enablePauseOnHover,
			keyNavigation,
			height,
			sliderSpeed,
			cardBorder,
			cardBorderHover,
			cardBoxShadow,
			cardBoxShadowHover,
			sliderStyle,
			slidesPerView,
			radius,

			enableArrow,
			arrowPosition,
			arrowHorizontalPlacement,
			arrowHeight,
			arrowWidth,
			arrowSize,
			arrowColor,
			arrowBackground,
			arrowHoverColor,
			arrowHoverBackground,
			enableArrowBorder,
			arrowBorder,
			arrowBoxShadow,
			arrowHoverBorder,
			arrowBoxShadowHover,

			category,
			tag,
			orderBy,
			orderType,
			authorName,
			postType,
			excludedCategory,
			postCount,

			postTitleTypography,
			postTitleMarkup,
			postTitleColor,
			postTitleHoverColor,
			postTitlePadding,
			enablePostTitleBorder,
			postTitleBorder,
			postTitleHoverBorder,

			enableCategory,
			headerMetaPosition,
			categoryColor,
			categoryBackground,
			categoryHoverColor,
			categoryHoverBackground,
			categoryPadding,
			categoryMargin,
			enableCategoryBorder,
			categoryBorder,
			categoryBoxShadow,
			categoryHoverBorder,
			categoryBoxShadowHover,
			categoryTypography,
			lineAccent,
			lineColor,

			metaPosition,
			metaJustification,
			enableAuthor,
			enableDate,
			enableReadTime,
			enableViewCount,
			enableIcon,
			metaIconColor,
			metaLinkColor,
			metaLinkHoverColor,
			metaTypography,
			enableMetaSeparator,
			metaSeparatorPosition,
			metaSeparatorColor,
			metaSeparatorColorHover,
			metaSeparatorPadding,
			metaPadding,

			excerptLimit,
			enableExcerpt,
			excerptColor,
			excerptMargin,
			excerptTypography,

			enableReadMore,
			readMoreText,
			readMoreColor,
			readMorewHoverColor,
			readMoreBackground,
			readMoreHoverBackground,
			readMoreSpacing,
			readMorePadding,
			enableReadMoreBorder,
			readMoreBorder,
			readMoreHoverBorder,
			readMoreBoxShadow,
			readMoreBoxShadowHover,

			enableViewMore,
			viewMoreText,
			viewButtonPosition,
			enableViewMoreIcon,
			viewMoreIcon,
			viewMoreColor,
			viewMoreHoverColor,
			viewMoreBackground,
			viewMoreHoverBackground,
			viewMoreSpacing,
			viewMorePadding,
			enableViewMoreBorder,
			viewMoreBorder,
			viewMoreHoverBorder,
			viewMoreBoxShadow,
			viewMoreBoxShadowHover,
			viewMoreLink,
			iconSize,
			viewAllTypography,

			enableDot,
			dotGap,
			dotHeight,
			dotWidth,
			horizontalPosition,
			verticalPosition,
			dotPosition,
			dotBackground,
			enableDotBorder,
			dotBorder,
			dotBoxShadow,
			dotHoverBorder,
			dotBoxShadowHover,

			blockPadding,
			blockMargin,
			cssID,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,

			sliderCardBackground,
			contentPadding,
			postBoxOverlay,
			postBoxPadding,
			postBoxBackground,
			postBoxHoverBackground,
			enablePostBoxBorder,
			postBoxBorder,
			postBoxHoverBorder,

			label,
			enableHeading,
			headingLayout,
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,
			headingColor,
			headingHoverColor,
			headingBorder,
			headingBorderBefore,
			headingBorderBottomInset,
			headingPadding,
			headingBackground,
			headingTypography,
			dotColor,
		},
		setAttributes,
		categories,
		tags,
		authorOptions,
		postTypeOptions,
	} = props;

	useEffect(() => {
		const type = wp.data.select("core/editor").getCurrentPost().type;

		if (type === "mzb-builder-template") {
			setAttributes({ postType: "mzb-builder" });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const headingLayoutsData = useMemo(
		() => [
			{
				id: "heading-layout-1",
				label: __("Layout 1", "magazine-blocks"),
				attrKey: "headingLayout1AdvancedStyle",
				value: headingLayout1AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-1-style-1",
						icon: HeadingLayout1Style1,
					},
				],
			},
			{
				id: "heading-layout-2",
				label: __("Layout 2", "magazine-blocks"),
				attrKey: "headingLayout2AdvancedStyle",
				value: headingLayout2AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-2-style-1",
						icon: HeadingLayout2Style1,
					},
				],
			},
			{
				id: "heading-layout-3",
				label: __("Layout 3", "magazine-blocks"),
				attrKey: "headingLayout3AdvancedStyle",
				value: headingLayout3AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-3-style-1",
						icon: HeadingLayout3Style1,
					},
				],
			},
			{
				id: "heading-layout-4",
				label: __("Layout 4", "magazine-blocks"),
				attrKey: "headingLayout4AdvancedStyle",
				value: headingLayout4AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-4-style-1",
						icon: HeadingLayout4Style1,
					},
				],
			},
			{
				id: "heading-layout-5",
				label: __("Layout 5", "magazine-blocks"),
				attrKey: "headingLayout5AdvancedStyle",
				value: headingLayout5AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-5-style-1",
						icon: HeadingLayout5Style1,
					},
				],
			},
			{
				id: "heading-layout-6",
				label: __("Layout 6", "magazine-blocks"),
				attrKey: "headingLayout6AdvancedStyle",
				value: headingLayout6AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-6-style-1",
						icon: HeadingLayout6Style1,
					},
				],
			},
			{
				id: "heading-layout-7",
				label: __("Layout 7", "magazine-blocks"),
				attrKey: "headingLayout7AdvancedStyle",
				value: headingLayout7AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-7-style-1",
						icon: HeadingLayout7Style1,
					},
				],
			},
			{
				id: "heading-layout-8",
				label: __("Layout 8", "magazine-blocks"),
				attrKey: "headingLayout8AdvancedStyle",
				value: headingLayout8AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-8-style-1",
						icon: HeadingLayout8Style1,
					},
				],
			},
			{
				id: "heading-layout-9",
				label: __("Layout 9", "magazine-blocks"),
				attrKey: "headingLayout9AdvancedStyle",
				value: headingLayout9AdvancedStyle,
				options: [
					{
						label: __("Style 1", "magazine-blocks"),
						value: "heading-layout-9-style-1",
						icon: HeadingLayout9Style1,
					},
				],
			},
		],
		[
			headingLayout1AdvancedStyle,
			headingLayout2AdvancedStyle,
			headingLayout3AdvancedStyle,
			headingLayout4AdvancedStyle,
			headingLayout5AdvancedStyle,
			headingLayout6AdvancedStyle,
			headingLayout7AdvancedStyle,
			headingLayout8AdvancedStyle,
			headingLayout9AdvancedStyle,
		]
	);

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Layout", "magazine-blocks")} initialOpen>
					<Select
						value={sliderStyle ?? "style1"}
						onChange={(val) => {
							setAttributes({ sliderStyle: val });
						}}
						label={__("Slider Style", "magazine-blocks")}
						options={[
							{
								label: __("Style1", "magazine-blocks"),
								value: "style1",
							},
							{
								label: __("Style2", "magazine-blocks"),
								value: "style2",
							},
							{
								label: __("Style3", "magazine-blocks"),
								value: "style3",
							},
							{
								label: __("Style4", "magazine-blocks"),
								value: "style4",
							},
						]}
					/>

					{(sliderStyle === "style3" || sliderStyle === "style4") && (
						<Slider
							label={__("Slides Per View", "magazine-blocks")}
							value={slidesPerView}
							step={1}
							min={1}
							max={4}
							onChange={(val) =>
								setAttributes({ slidesPerView: val })
							}
						/>
					)}
					<ToggleButtonGroup
						value={alignment}
						onChange={(v) => {
							setAttributes({ alignment: v });
						}}
						label={__("Content Alignment", "magazine-blocks")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<TextAlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<TextAlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<TextAlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
					<Toggle
						checked={enableAutoPlay}
						onChange={() =>
							setAttributes({
								enableAutoPlay: !enableAutoPlay,
							})
						}
						label={__("Auto Play", "magazine-blocks")}
					/>
					<Toggle
						checked={enablePauseOnHover}
						onChange={() =>
							setAttributes({
								enablePauseOnHover: !enablePauseOnHover,
							})
						}
						label={__("Pause On Hover", "magazine-blocks")}
					/>
					<Toggle
						checked={keyNavigation}
						onChange={() =>
							setAttributes({
								keyNavigation: !keyNavigation,
							})
						}
						label={__("Keyboard Navigation", "magazine-blocks")}
					/>
					<Slider
						onChange={(val: any) => setAttributes({ height: val })}
						label={__("Height", "magazine-blocks")}
						units={["px", "em", "%"]}
						responsive={true}
						min={200}
						max={1000}
						value={height}
						resetAttributeKey="height"
					/>
					<Slider
						onChange={(val: any) =>
							setAttributes({ sliderSpeed: val })
						}
						label={__("Speed", "magazine-blocks")}
						min={0}
						max={10000}
						value={sliderSpeed}
						resetAttributeKey="sliderSpeed"
					/>
				</Panel>
				<Panel title={__("Post Options", "magazine-blocks")}>
					<Select
						options={postTypeOptions ?? []}
						onChange={(val) => setAttributes({ postType: val })}
						value={postType}
						label={__("Post Type", "magazine-blocks")}
					/>
					<Slider
						label={__("Post Count", "magazine-blocks")}
						value={postCount}
						step={1}
						min={1}
						max={15}
						onChange={(val) => setAttributes({ postCount: val })}
						resetAttributeKey="postCount"
					/>
					<>
						<ToggleButtonGroup
							value={orderType}
							onChange={(v) => {
								setAttributes({ orderType: v });
							}}
							label={__("Order", "magazine-blocks")}
							groupProps={{
								sx: {
									".chakra-button": {
										minH: "32px",
										color: "#1A202C",
										svg: {
											w: 6,
											h: 6,
										},
										"&[data-active]": {
											color: "#fff !important",
										},
									},
								},
							}}
						>
							<ToggleButton value="asc">Ascending</ToggleButton>
							<ToggleButton value="desc">Descending</ToggleButton>
						</ToggleButtonGroup>
					</>
					<Select
						options={[
							{
								label: __("Date", "magazine-blocks"),
								value: "date",
							},
							{
								label: __("Title", "magazine-blocks"),
								value: "title",
							},
							{
								label: __("Random", "magazine-blocks"),
								value: "rand",
							},
						]}
						onChange={(val) => setAttributes({ orderBy: val })}
						value={orderBy}
						label={__("Order By", "magazine-blocks")}
					/>
					{postType !== "mzb-builder" && (
						<>
							<Select
								value={category}
								onChange={(val) =>
									setAttributes({ category: val })
								}
								label={__("Category", "magazine-blocks")}
								options={[
									{ value: "all", label: __("All") },
									...(categories ?? [])?.map(
										({ name, id }) => ({
											label: name,
											value: id.toString(),
										})
									),
								]}
							/>

							<Select
								isMulti
								value={excludedCategory}
								onChange={(val) =>
									setAttributes({ excludedCategory: val })
								}
								label={__(
									"Excluded Category",
									"magazine-blocks"
								)}
								options={[
									{ value: "all", label: __("All") },
									...(categories ?? [])?.map(
										({ name, id }) => ({
											label: name,
											value: id.toString(),
										})
									),
								]}
								//@ts-ignore
								multiple={true}
							/>
							<Select
								value={tag}
								onChange={(val) => setAttributes({ tag: val })}
								label={__("Tag", "magazine-blocks")}
								options={[
									{ value: "all", label: __("All") },
									...(tags ?? [])?.map(({ name, id }) => ({
										label: name,
										value: id.toString(),
									})),
								]}
							/>
							<Select
								options={[
									{
										value: "all",
										label: __("All", "magazine-blocks"),
									},
									...(authorOptions ?? []),
								]}
								onChange={(val) =>
									setAttributes({ authorName: val })
								}
								value={authorName}
								label={__("Authors", "magazine-blocks")}
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Heading", "magazine-blocks")}>
					<Toggle
						checked={enableHeading}
						onChange={() =>
							setAttributes({ enableHeading: !enableHeading })
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableHeading && (
						<>
							<Input
								onChange={(val) =>
									setAttributes({ label: val })
								}
								label={__("Label", "magazine-blocks")}
								value={label || ""}
							/>
							<LayoutOptionsRenderer
								columns={2}
								layoutsData={headingLayoutsData}
								selectedValues={Object.fromEntries(
									headingLayoutsData.map((ld) => [
										ld.attrKey,
										ld.value,
									])
								)}
								onChange={(attrKey, value, layoutId) =>
									setAttributes({
										...clearOtherAdvancedStyles(
											headingLayoutsData,
											attrKey
										),
										[attrKey]: value,
										headingLayout: layoutId,
									})
								}
								currentLayout={headingLayout}
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Arrow", "magazine-blocks")}>
					<Toggle
						checked={enableArrow}
						onChange={() =>
							setAttributes({ enableArrow: !enableArrow })
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableArrow && (
						<>
							<Select
								options={[
									{
										label: __("Top", "magazine-blocks"),
										value: "top",
									},
									{
										label: __("Center", "magazine-blocks"),
										value: "center",
									},
									{
										label: __("Bottom", "magazine-blocks"),
										value: "bottom",
									},
								]}
								onChange={(val) =>
									setAttributes({ arrowPosition: val })
								}
								value={arrowPosition}
								label={__("Arrow Position", "magazine-blocks")}
							/>
						</>
					)}
					{enableArrow && arrowPosition === "center" && (
						<Select
							options={[
								{
									label: __(
										"Inside Image",
										"magazine-blocks"
									),
									value: "inside",
								},
								{
									label: __(
										"Outside Image",
										"magazine-blocks"
									),
									value: "outside",
								},
								{
									label: __(
										"In between Image",
										"magazine-blocks"
									),
									value: "in-between",
								},
							]}
							onChange={(val) =>
								setAttributes({
									arrowHorizontalPlacement: val,
								})
							}
							value={arrowHorizontalPlacement}
							label={__(
								"Arrow Horizontal Placement",
								"magazine-blocks"
							)}
						/>
					)}
				</Panel>
				<Panel title={__("Post Title", "magazine-blocks")}>
					<Select
						options={[
							{
								label: __("H1", "magazine-blocks"),
								value: "h1",
							},
							{
								label: __("H2", "magazine-blocks"),
								value: "h2",
							},
							{
								label: __("H3", "magazine-blocks"),
								value: "h3",
							},
							{
								label: __("H4", "magazine-blocks"),
								value: "h4",
							},
							{
								label: __("H5", "magazine-blocks"),
								value: "h5",
							},
							{
								label: __("H6", "magazine-blocks"),
								value: "h6",
							},
						]}
						onChange={(val) =>
							setAttributes({ postTitleMarkup: val })
						}
						value={postTitleMarkup}
						label={__("HTML Markup", "magazine-blocks")}
					/>
				</Panel>
				<Panel title={__("Header Meta", "magazine-blocks")}>
					<Toggle
						checked={enableCategory}
						onChange={() =>
							setAttributes({
								enableCategory: !enableCategory,
							})
						}
						label={__("Category", "magazine-blocks")}
					/>
					{enableCategory && sliderStyle === "style3" && (
						<Select
							options={[
								{
									label: __(
										"Inside Image",
										"magazine-blocks"
									),
									value: "inside-image",
								},
								{
									label: __(
										"Outside Image",
										"magazine-blocks"
									),
									value: "out-image",
								},
							]}
							onChange={(val) =>
								setAttributes({ headerMetaPosition: val })
							}
							value={headerMetaPosition}
							label={__(
								"Header Meta Position",
								"magazine-blocks"
							)}
						/>
					)}
				</Panel>
				<Panel title={__("Meta", "magazine-blocks")}>
					<ToggleButtonGroup
						value={metaPosition}
						onChange={(v) => {
							setAttributes({ metaPosition: v });
						}}
						label={__("Position", "magazine-blocks")}
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
							},
						}}
					>
						<ToggleButton value="top">
							<LayoutAlignTop />
						</ToggleButton>
						<ToggleButton value="bottom">
							<LayoutAlignBottom />
						</ToggleButton>
					</ToggleButtonGroup>
					<ToggleButtonGroup
						value={metaJustification}
						onChange={(v) => {
							setAttributes({ metaJustification: v });
						}}
						label={__("Justification", "magazine-blocks")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									"&[data-active]": {
										svg: {
											fill: "#fff !important",
										},
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<AlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<AlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<AlignRight />
						</ToggleButton>
						<ToggleButton value="space-between">
							<AlignSpaceBetween />
						</ToggleButton>
					</ToggleButtonGroup>
					<Toggle
						checked={enableAuthor}
						onChange={() =>
							setAttributes({
								enableAuthor: !enableAuthor,
							})
						}
						label={__("Author", "magazine-blocks")}
					/>
					<Toggle
						checked={enableDate}
						onChange={() =>
							setAttributes({ enableDate: !enableDate })
						}
						label={__("Date", "magazine-blocks")}
					/>
					<Toggle
						checked={enableReadTime}
						onChange={() =>
							setAttributes({ enableReadTime: !enableReadTime })
						}
						label={__("Read Time", "magazine-blocks")}
					/>
					<Toggle
						checked={enableViewCount}
						onChange={() =>
							setAttributes({ enableViewCount: !enableViewCount })
						}
						label={__("View Count", "magazine-blocks")}
					/>
					<Toggle
						checked={enableIcon}
						onChange={() =>
							setAttributes({ enableIcon: !enableIcon })
						}
						label={__("Icon", "magazine-blocks")}
					/>
					{sliderStyle === "style3" && (
						<>
							<Toggle
								checked={enableMetaSeparator}
								onChange={() =>
									setAttributes({
										enableMetaSeparator:
											!enableMetaSeparator,
									})
								}
								label={__("Separator", "magazine-blocks")}
							/>
							{enableMetaSeparator && (
								<>
									<Select
										options={[
											{
												label: __(
													"Top",
													"magazine-blocks"
												),
												value: "top",
											},
											{
												label: __(
													"Bottom",
													"magazine-blocks"
												),
												value: "bottom",
											},
										]}
										onChange={(val) =>
											setAttributes({
												metaSeparatorPosition: val,
											})
										}
										value={metaSeparatorPosition}
										label={__(
											"Separator Position",
											"magazine-blocks"
										)}
									/>
								</>
							)}
						</>
					)}
				</Panel>
				<Panel title={__("Excerpt", "magazine-blocks")}>
					<Toggle
						checked={enableExcerpt}
						onChange={() =>
							setAttributes({
								enableExcerpt: !enableExcerpt,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableExcerpt && (
						<Slider
							onChange={(val) =>
								setAttributes({ excerptLimit: val })
							}
							label={__("Limit", "magazine-blocks")}
							min={0}
							max={500}
							step={1}
							value={excerptLimit}
							resetAttributeKey="excerptLimit"
						/>
					)}
				</Panel>
				<Panel title={__("Read More", "magazine-blocks")}>
					<Toggle
						checked={enableReadMore}
						onChange={() =>
							setAttributes({
								enableReadMore: !enableReadMore,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableReadMore && (
						<Input
							value={readMoreText}
							label={__("Text", "magazine-blocks")}
							onChange={(val) =>
								setAttributes({ readMoreText: val })
							}
						/>
					)}
				</Panel>
				<Panel title={__("View All", "magazine-blocks")}>
					<Toggle
						checked={enableViewMore}
						onChange={() =>
							setAttributes({
								enableViewMore: !enableViewMore,
							})
						}
						label={__("Enable", "magazine-blocks")}
					/>
					{enableViewMore && (
						<>
							<Input
								value={viewMoreText}
								label={__("Text", "magazine-blocks")}
								onChange={(val) =>
									setAttributes({ viewMoreText: val })
								}
							/>
							<Select
								options={[
									{
										label: __("Top", "magazine-blocks"),
										value: "top",
									},
									{
										label: __("Bottom", "magazine-blocks"),
										value: "bottom",
									},
								]}
								onChange={(val) =>
									setAttributes({ viewButtonPosition: val })
								}
								value={viewButtonPosition}
								label={__(
									"View Button Position",
									"magazine-blocks"
								)}
							/>
							<Toggle
								checked={enableViewMoreIcon}
								onChange={() =>
									setAttributes({
										enableViewMoreIcon: !enableViewMoreIcon,
									})
								}
								label={__("Enable Icon", "magazine-blocks")}
							/>
							{enableViewMoreIcon && (
								<IconPicker
									value={viewMoreIcon}
									onChange={(val) =>
										setAttributes({ viewMoreIcon: val })
									}
								/>
							)}
							<UrlInput
								onChange={(val) =>
									setAttributes({ viewMoreLink: val })
								}
								label={__("View More Link", "magazine-blocks")}
								value={viewMoreLink}
								newTab
								noFollow
							/>
						</>
					)}
				</Panel>
				<Panel title={__("Dots", "magazine-blocks")}>
					<Toggle
						checked={enableDot}
						onChange={() =>
							setAttributes({ enableDot: !enableDot })
						}
						label={__("Enable", "magazine-blocks")}
					/>

					{enableDot && (
						<Select
							value={dotPosition ?? "inside-swiper"}
							onChange={(val) => {
								setAttributes({ dotPosition: val });
							}}
							label={__(" Position", "magazine-blocks")}
							options={[
								{
									label: __(
										"Inside Swiper",
										"magazine-blocks"
									),
									value: "inside-swiper",
								},
								{
									label: __(
										"Outside Swiper",
										"magazine-blocks"
									),
									value: "outside-swiper",
								},
							]}
						/>
					)}
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("General", "magazine-blocks")} initialOpen>
					{sliderStyle === "style3" && (
						<>
							<PopoverDrawer
								label={__("Background", "magazine-blocks")}
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={sliderCardBackground}
												resetKey="sliderCardBackground"
												onReset={(v) => {
													setAttributes({
														sliderCardBackground: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<Background
												value={sliderCardBackground}
												onChange={(v) =>
													setAttributes({
														sliderCardBackground: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						</>
					)}
					<Dimensions
						value={contentPadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({ contentPadding: val })
						}
						resetAttributeKey="contentPadding"
					/>
				</Panel>
				{enableArrow && (
					<Panel title={__("Arrow", "magazine-blocks")}>
						<Slider
							onChange={(val: any) =>
								setAttributes({ arrowHeight: val })
							}
							label={__("Height", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={60}
							value={arrowHeight}
							resetAttributeKey="arrowHeight"
						/>
						<Slider
							onChange={(val: any) =>
								setAttributes({ arrowWidth: val })
							}
							label={__("Width", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={60}
							value={arrowWidth}
							resetAttributeKey="arrowWidth"
						/>
						<Slider
							onChange={(val: any) =>
								setAttributes({ arrowSize: val })
							}
							label={__("Size", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={100}
							value={arrowSize}
							resetAttributeKey="arrowSize"
						/>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: arrowColor,
												id: "arrowColor",
											},
											{
												color: arrowHoverColor,
												id: "arrowHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={arrowColor}
											resetKey="arrowColor"
											onReset={(v) => {
												setAttributes({
													arrowColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={arrowColor}
											onChange={(v) =>
												setAttributes({ arrowColor: v })
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={arrowHoverColor}
											resetKey="arrowHoverColor"
											onReset={(v) => {
												setAttributes({
													arrowHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={arrowHoverColor}
											onChange={(v) =>
												setAttributes({
													arrowHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={arrowBackground}
											resetKey="arrowBackground"
											onReset={(v) => {
												setAttributes({
													arrowBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={arrowBackground}
											onChange={(v) =>
												setAttributes({
													arrowBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={arrowHoverBackground}
											resetKey="arrowHoverBackground"
											onReset={(v) => {
												setAttributes({
													arrowHoverBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={arrowHoverBackground}
											onChange={(v) =>
												setAttributes({
													arrowHoverBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Toggle
							checked={enableArrowBorder}
							onChange={() =>
								setAttributes({
									enableArrowBorder: !enableArrowBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enableArrowBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={arrowBorder}
											onChange={(v) => {
												setAttributes({
													arrowBorder: v,
												});
											}}
											resetAttributeKey="arrowBorder"
										/>
										<BoxShadow
											resetAttributeKey="arrowBoxShadow"
											value={arrowBoxShadow}
											onChange={(val) =>
												setAttributes({
													arrowBoxShadow: val,
												})
											}
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={arrowHoverBorder}
											onChange={(v) => {
												setAttributes({
													arrowHoverBorder: v,
												});
											}}
											resetAttributeKey="arrowHoverBorder"
										/>
										<BoxShadow
											resetAttributeKey="arrowBoxShadowHover"
											value={arrowBoxShadowHover}
											onChange={(val) =>
												setAttributes({
													arrowBoxShadowHover: val,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
					</Panel>
				)}
				{enableHeading && (
					<Panel title={__("Heading", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Heading Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: headingColor,
												id: "headingColor",
											},
											{
												color: headingHoverColor,
												id: "headingHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={headingColor}
											resetKey="headingColor"
											onReset={(v) => {
												setAttributes({
													headingColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={headingColor}
											onChange={(v) =>
												setAttributes({
													headingColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={headingHoverColor}
											resetKey="headingHoverColor"
											onReset={(v) => {
												setAttributes({
													headingHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={headingHoverColor}
											onChange={(v) =>
												setAttributes({
													headingHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						{("heading-layout-6" === headingLayout ||
							"heading-layout-7" === headingLayout) && (
							<>
								<Dimensions
									value={headingPadding}
									responsive
									label={__("Padding", "magazine-blocks")}
									resetAttributeKey="headingPadding"
									type="padding"
									defaultUnit="px"
									units={["px", "rem", "em", "%"]}
									onChange={(val) =>
										setAttributes({
											headingPadding: val,
										})
									}
								/>
							</>
						)}
						<Typography
							value={headingTypography}
							onChange={(val) =>
								setAttributes({ headingTypography: val })
							}
							resetAttributeKey="headingTypography"
						/>

						{"heading-layout-2" === headingLayout && (
							<PopoverDrawer
								label={__("Background", "magazine-blocks")}
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={headingBackground}
												resetKey="headingBackground"
												onReset={(v) => {
													setAttributes({
														headingBackground: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<Background
												value={headingBackground}
												onChange={(v) =>
													setAttributes({
														headingBackground: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						)}

						{("heading-layout-1" === headingLayout ||
							"heading-layout-3" === headingLayout ||
							"heading-layout-7" === headingLayout ||
							"heading-layout-8" === headingLayout) && (
							<>
								<PopoverDrawer
									label={__("Line Accent", "magazine-blocks")}
									trigger={(props) => (
										<PopoverDrawer.Trigger {...props}>
											<ZStack
												items={[
													{
														color: lineAccent,
														id: "lineAccent",
													},
												]}
											/>
										</PopoverDrawer.Trigger>
									)}
									closeOnFocusOutside
								>
									<Tabs position="relative">
										<TabList>
											<Tab>
												{__(
													"Normal",
													"magazine-blocks"
												)}
											</Tab>
										</TabList>
										<TabPanels>
											<TabPanel>
												<Reset
													saved={lineAccent}
													resetKey="lineAccent"
													onReset={(v) => {
														setAttributes({
															lineAccent: v,
														});
													}}
													buttonProps={{
														position: "absolute",
														top: "12px",
														right: "10px",
													}}
												/>
												<ColorPicker
													value={lineAccent}
													onChange={(v) =>
														setAttributes({
															lineAccent: v,
														})
													}
												/>
											</TabPanel>
										</TabPanels>
									</Tabs>
								</PopoverDrawer>
							</>
						)}

						{("heading-layout-2" === headingLayout ||
							"heading-layout-3" === headingLayout ||
							"heading-layout-4" === headingLayout ||
							"heading-layout-5" === headingLayout ||
							"heading-layout-5" === headingLayout ||
							"heading-layout-6" === headingLayout ||
							"heading-layout-7" === headingLayout ||
							"heading-layout-9" === headingLayout) && (
							<>
								<PopoverDrawer
									label={__("Line Color", "magazine-blocks")}
									trigger={(props) => (
										<PopoverDrawer.Trigger {...props}>
											<ZStack
												items={[
													{
														color: lineColor,
														id: "lineColor",
													},
												]}
											/>
										</PopoverDrawer.Trigger>
									)}
									closeOnFocusOutside
								>
									<Tabs position="relative">
										<TabList>
											<Tab>
												{__(
													"Normal",
													"magazine-blocks"
												)}
											</Tab>
										</TabList>
										<TabPanels>
											<TabPanel>
												<Reset
													saved={lineColor}
													resetKey="lineColor"
													onReset={(v) => {
														setAttributes({
															lineColor: v,
														});
													}}
													buttonProps={{
														position: "absolute",
														top: "12px",
														right: "10px",
													}}
												/>
												<ColorPicker
													value={lineColor}
													onChange={(v) =>
														setAttributes({
															lineColor: v,
														})
													}
												/>
											</TabPanel>
										</TabPanels>
									</Tabs>
								</PopoverDrawer>
							</>
						)}
						{("heading-layout-4" === headingLayout ||
							"heading-layout-5" === headingLayout) && (
							<PopoverDrawer
								label={__("Dot Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: dotColor,
													id: "dotColor",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={dotColor}
												resetKey="dotColor"
												onReset={(v) => {
													setAttributes({
														dotColor: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={dotColor}
												onChange={(v) =>
													setAttributes({
														dotColor: v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
						)}
					</Panel>
				)}
				{sliderStyle === "style3" && (
					<Panel title={__("Post Box", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={postBoxBackground}
											resetKey="postBoxBackground"
											onReset={(v) => {
												setAttributes({
													postBoxBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={postBoxBackground}
											onChange={(v) =>
												setAttributes({
													postBoxBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={postBoxHoverBackground}
											resetKey="postBoxHoverBackground"
											onReset={(v) => {
												setAttributes({
													postBoxHoverBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={postBoxHoverBackground}
											onChange={(v) =>
												setAttributes({
													postBoxHoverBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Dimensions
							value={postBoxPadding}
							responsive
							label={__("Padding", "magazine-blocks")}
							type="padding"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ postBoxPadding: val })
							}
							resetAttributeKey="postBoxPadding"
						/>
						<Toggle
							checked={enablePostBoxBorder}
							onChange={() =>
								setAttributes({
									enablePostBoxBorder: !enablePostBoxBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enablePostBoxBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={postBoxBorder}
											onChange={(v) => {
												setAttributes({
													postBoxBorder: v,
												});
											}}
											resetAttributeKey="postBoxBorder"
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={postBoxHoverBorder}
											onChange={(v) => {
												setAttributes({
													postBoxHoverBorder: v,
												});
											}}
											resetAttributeKey="postBoxHoverBorder"
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
					</Panel>
				)}
				{(sliderStyle === "style1" || sliderStyle === "style4") && (
					<Panel title={__("Post Box", "magazine-blocks")}>
						<PopoverDrawer label={__("Overlay", "magazine-blocks")}>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={postBoxOverlay}
											resetKey="postBoxOverlay"
											onReset={(v) => {
												setAttributes({
													postBoxOverlay: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={postBoxOverlay}
											onChange={(v) =>
												setAttributes({
													postBoxOverlay: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
					</Panel>
				)}
				<Panel title={__("Post Title", "magazine-blocks")}>
					<Typography
						value={postTitleTypography}
						onChange={(val) =>
							setAttributes({ postTitleTypography: val })
						}
						resetAttributeKey="postTitleTypography"
					/>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: postTitleColor,
											id: "postTitleColor",
										},
										{
											color: postTitleHoverColor,
											id: "postTitleHoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={postTitleColor}
										resetKey="postTitleColor"
										onReset={(v) => {
											setAttributes({
												postTitleColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={postTitleColor}
										onChange={(v) =>
											setAttributes({ postTitleColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={postTitleHoverColor}
										resetKey="postTitleHoverColor"
										onReset={(v) => {
											setAttributes({
												postTitleHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={postTitleHoverColor}
										onChange={(v) =>
											setAttributes({
												postTitleHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Dimensions
						value={postTitlePadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({ postTitlePadding: val })
						}
						resetAttributeKey="postTitlePadding"
					/>
					<Toggle
						checked={enablePostTitleBorder}
						onChange={() =>
							setAttributes({
								enablePostTitleBorder: !enablePostTitleBorder,
							})
						}
						label={__("Border", "magazine-blocks")}
					/>
					{enablePostTitleBorder && (
						<Tabs isFitted variant="unstyled">
							<TabList
								border="1px solid #E0E0E0"
								p="4px"
								borderRadius="2px"
							>
								<Tab
									_selected={{
										color: "white",
										bg: "#7E36F4",
										borderRadius: "2px",
									}}
								>
									Normal
								</Tab>
								<Tab
									_selected={{
										color: "white",
										bg: "#7E36F4",
										borderRadius: "2px",
									}}
								>
									Hover
								</Tab>
							</TabList>
							<TabPanels>
								<TabPanel p="16px 0">
									<Border
										value={postTitleBorder}
										onChange={(v) => {
											setAttributes({
												postTitleBorder: v,
											});
										}}
										resetAttributeKey="postTitleBorder"
									/>
								</TabPanel>
								<TabPanel p="16px 0">
									<Border
										value={postTitleHoverBorder}
										onChange={(v) => {
											setAttributes({
												postTitleHoverBorder: v,
											});
										}}
										resetAttributeKey="postTitleHoverBorder"
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					)}
				</Panel>
				<Panel title={__("Header Meta", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: categoryColor,
											id: "categoryColor",
										},
										{
											color: categoryHoverColor,
											id: "categoryHoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={categoryColor}
										resetKey="categoryColor"
										onReset={(v) => {
											setAttributes({ categoryColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={categoryColor}
										onChange={(v) =>
											setAttributes({ categoryColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={categoryHoverColor}
										resetKey="categoryHoverColor"
										onReset={(v) => {
											setAttributes({
												categoryHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={categoryHoverColor}
										onChange={(v) =>
											setAttributes({
												categoryHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Typography
						value={categoryTypography}
						onChange={(val) =>
							setAttributes({
								categoryTypography: val,
							})
						}
						resetAttributeKey="categoryTypography"
					/>
					<PopoverDrawer label={__("Background", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={categoryBackground}
										resetKey="categoryBackground"
										onReset={(v) => {
											setAttributes({
												categoryBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={categoryBackground}
										onChange={(v) =>
											setAttributes({
												categoryBackground: v,
											})
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={categoryHoverBackground}
										resetKey="categoryHoverBackground"
										onReset={(v) => {
											setAttributes({
												categoryHoverBackground: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={categoryHoverBackground}
										onChange={(v) =>
											setAttributes({
												categoryHoverBackground: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Dimensions
						value={categoryPadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({ categoryPadding: val })
						}
						resetAttributeKey="categoryPadding"
					/>
					<Dimensions
						value={categoryMargin}
						responsive
						label={__("Margin", "magazine-blocks")}
						type="margin"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) =>
							setAttributes({ categoryMargin: val })
						}
						resetAttributeKey="categoryMargin"
					/>
					<Toggle
						checked={enableCategoryBorder}
						onChange={() =>
							setAttributes({
								enableCategoryBorder: !enableCategoryBorder,
							})
						}
						label={__("Border", "magazine-blocks")}
					/>
					{enableCategoryBorder && (
						<Tabs isFitted variant="unstyled">
							<TabList
								border="1px solid #E0E0E0"
								p="4px"
								borderRadius="2px"
							>
								<Tab
									_selected={{
										color: "white",
										bg: "#7E36F4",
										borderRadius: "2px",
									}}
								>
									Normal
								</Tab>
								<Tab
									_selected={{
										color: "white",
										bg: "#7E36F4",
										borderRadius: "2px",
									}}
								>
									Hover
								</Tab>
							</TabList>
							<TabPanels>
								<TabPanel p="16px 0">
									<Border
										value={categoryBorder}
										onChange={(v) => {
											setAttributes({
												categoryBorder: v,
											});
										}}
										resetAttributeKey="categoryBorder"
									/>
									<BoxShadow
										resetAttributeKey="categoryBoxShadow"
										value={categoryBoxShadow}
										onChange={(val) =>
											setAttributes({
												categoryBoxShadow: val,
											})
										}
									/>
								</TabPanel>
								<TabPanel p="16px 0">
									<Border
										value={categoryHoverBorder}
										onChange={(v) => {
											setAttributes({
												categoryHoverBorder: v,
											});
										}}
										resetAttributeKey="categoryHoverBorder"
									/>
									<BoxShadow
										resetAttributeKey="categoryBoxShadowHover"
										value={categoryBoxShadowHover}
										onChange={(val) =>
											setAttributes({
												categoryBoxShadowHover: val,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					)}
				</Panel>
				<Panel title={__("Meta", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: metaIconColor,
											id: "metaIconColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={metaIconColor}
										resetKey="metaIconColor"
										onReset={(v) => {
											setAttributes({ metaIconColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaIconColor}
										onChange={(v) =>
											setAttributes({ metaIconColor: v })
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<PopoverDrawer
						label={__("Link Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{ color: metaLinkColor, id: "color" },
										{
											color: metaLinkHoverColor,
											id: "hoverColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={metaLinkColor}
										resetKey="metaLinkColor"
										onReset={(v) => {
											setAttributes({ metaLinkColor: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaLinkColor}
										onChange={(v) =>
											setAttributes({ metaLinkColor: v })
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={metaLinkHoverColor}
										resetKey="metaLinkHoverColor"
										onReset={(v) => {
											setAttributes({
												metaLinkHoverColor: v,
											});
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										value={metaLinkHoverColor}
										onChange={(v) =>
											setAttributes({
												metaLinkHoverColor: v,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Typography
						value={metaTypography}
						onChange={(val) =>
							setAttributes({
								metaTypography: val,
							})
						}
						resetAttributeKey="metaTypography"
					/>
					{enableMetaSeparator && (
						<>
							<PopoverDrawer
								label={__("Separator Color", "magazine-blocks")}
								trigger={(props) => (
									<PopoverDrawer.Trigger {...props}>
										<ZStack
											items={[
												{
													color: metaSeparatorColor,
													id: "metaSeparatorColor",
												},
												{
													color: metaSeparatorColorHover,
													id: "metaSeparatorColorHover",
												},
											]}
										/>
									</PopoverDrawer.Trigger>
								)}
								closeOnFocusOutside
							>
								<Tabs position="relative">
									<TabList>
										<Tab>
											{__("Normal", "magazine-blocks")}
										</Tab>
										<Tab>
											{__("Hover", "magazine-blocks")}
										</Tab>
									</TabList>
									<TabPanels>
										<TabPanel>
											<Reset
												saved={metaSeparatorColor}
												resetKey="metaSeparatorColor"
												onReset={(v) => {
													setAttributes({
														metaSeparatorColor: v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={metaSeparatorColor}
												onChange={(v) =>
													setAttributes({
														metaSeparatorColor: v,
													})
												}
											/>
										</TabPanel>
										<TabPanel>
											<Reset
												saved={metaSeparatorColorHover}
												resetKey="metaSeparatorColorHover"
												onReset={(v) => {
													setAttributes({
														metaSeparatorColorHover:
															v,
													});
												}}
												buttonProps={{
													position: "absolute",
													top: "12px",
													right: "10px",
												}}
											/>
											<ColorPicker
												value={metaSeparatorColorHover}
												onChange={(v) =>
													setAttributes({
														metaSeparatorColorHover:
															v,
													})
												}
											/>
										</TabPanel>
									</TabPanels>
								</Tabs>
							</PopoverDrawer>
							<Dimensions
								label={__(
									"Separator Padding",
									"magazine-blocks"
								)}
								value={metaSeparatorPadding}
								onChange={(val) =>
									setAttributes({
										metaSeparatorPadding: val,
									})
								}
								responsive
								type="padding"
								defaultUnit="px"
								units={["px", "rem", "em", "%"]}
								resetAttributeKey="metaSeparatorPadding"
							/>
						</>
					)}
					<Dimensions
						value={metaPadding}
						responsive
						label={__("Padding", "magazine-blocks")}
						type="padding"
						defaultUnit="px"
						units={["px", "rem", "em", "%"]}
						onChange={(val) => setAttributes({ metaPadding: val })}
						resetAttributeKey="metaPadding"
					/>
				</Panel>
				{enableExcerpt && (
					<Panel title={__("Excerpt", "magazine-blocks")}>
						<Typography
							value={excerptTypography}
							onChange={(val) =>
								setAttributes({
									excerptTypography: val,
								})
							}
							resetAttributeKey="excerptTypography"
						/>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: excerptColor,
												id: "color",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={excerptColor}
											resetKey="excerptColor"
											onReset={(v) => {
												setAttributes({
													excerptColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={excerptColor}
											onChange={(v) =>
												setAttributes({
													excerptColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Dimensions
							value={excerptMargin}
							responsive
							label={__("Margin", "magazine-blocks")}
							type="margin"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ excerptMargin: val })
							}
							resetAttributeKey="excerptMargin"
						/>
					</Panel>
				)}
				{enableReadMore && (
					<Panel title={__("Read More", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: readMoreColor,
												id: "readMoreColor",
											},
											{
												color: readMorewHoverColor,
												id: "readMorewHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={readMoreColor}
											resetKey="readMoreColor"
											onReset={(v) => {
												setAttributes({
													readMoreColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={readMoreColor}
											onChange={(v) =>
												setAttributes({
													readMoreColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={readMorewHoverColor}
											resetKey="readMorewHoverColor"
											onReset={(v) => {
												setAttributes({
													readMorewHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={readMorewHoverColor}
											onChange={(v) =>
												setAttributes({
													readMorewHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={readMoreBackground}
											resetKey="readMoreBackground"
											onReset={(v) => {
												setAttributes({
													readMoreBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={readMoreBackground}
											onChange={(v) =>
												setAttributes({
													readMoreBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={readMoreHoverBackground}
											resetKey="readMoreHoverBackground"
											onReset={(v) => {
												setAttributes({
													readMoreHoverBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={readMoreHoverBackground}
											onChange={(v) =>
												setAttributes({
													readMoreHoverBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Slider
							onChange={(val: any) =>
								setAttributes({ readMoreSpacing: val })
							}
							label={__("Spacing", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={100}
							value={readMoreSpacing}
							resetAttributeKey="readMoreSpacing"
						/>
						<Dimensions
							value={readMorePadding}
							responsive
							label={__("Padding", "magazine-blocks")}
							type="margin"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ readMorePadding: val })
							}
							resetAttributeKey="readMorePadding"
						/>
						<Toggle
							checked={enableReadMoreBorder}
							onChange={() =>
								setAttributes({
									enableReadMoreBorder: !enableReadMoreBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enableReadMoreBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={readMoreBorder}
											onChange={(v) => {
												setAttributes({
													readMoreBorder: v,
												});
											}}
											resetAttributeKey="readMoreBorder"
										/>
										<BoxShadow
											resetAttributeKey="readMoreBoxShadow"
											value={readMoreBoxShadow}
											onChange={(val) =>
												setAttributes({
													readMoreBoxShadow: val,
												})
											}
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={readMoreHoverBorder}
											onChange={(v) => {
												setAttributes({
													readMoreHoverBorder: v,
												});
											}}
											resetAttributeKey="readMoreHoverBorder"
										/>
										<BoxShadow
											resetAttributeKey="readMoreBoxShadowHover"
											value={readMoreBoxShadowHover}
											onChange={(val) =>
												setAttributes({
													readMoreBoxShadowHover: val,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
					</Panel>
				)}
				{enableViewMore && (
					<Panel title={__("View All", "magazine-blocks")}>
						<PopoverDrawer
							label={__("Text Color", "magazine-blocks")}
							trigger={(props) => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color: viewMoreColor,
												id: "viewMoreColor",
											},
											{
												color: viewMoreHoverColor,
												id: "viewMoreHoverColor",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							)}
							closeOnFocusOutside
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={viewMoreColor}
											resetKey="viewMoreColor"
											onReset={(v) => {
												setAttributes({
													viewMoreColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={viewMoreColor}
											onChange={(v) =>
												setAttributes({
													viewMoreColor: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={viewMoreHoverColor}
											resetKey="viewMoreHoverColor"
											onReset={(v) => {
												setAttributes({
													viewMoreHoverColor: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<ColorPicker
											value={viewMoreHoverColor}
											onChange={(v) =>
												setAttributes({
													viewMoreHoverColor: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
									<Tab>{__("Hover", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={viewMoreBackground}
											resetKey="viewMoreBackground"
											onReset={(v) => {
												setAttributes({
													viewMoreBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={viewMoreBackground}
											onChange={(v) =>
												setAttributes({
													viewMoreBackground: v,
												})
											}
										/>
									</TabPanel>
									<TabPanel>
										<Reset
											saved={viewMoreHoverBackground}
											resetKey="viewMoreHoverBackground"
											onReset={(v) => {
												setAttributes({
													viewMoreHoverBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={viewMoreHoverBackground}
											onChange={(v) =>
												setAttributes({
													viewMoreHoverBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Slider
							onChange={(val: any) =>
								setAttributes({ viewMoreSpacing: val })
							}
							label={__("Spacing", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={100}
							value={viewMoreSpacing}
							resetAttributeKey="viewMoreSpacing"
						/>
						<Dimensions
							value={viewMorePadding}
							responsive
							label={__("Padding", "magazine-blocks")}
							type="margin"
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) =>
								setAttributes({ viewMorePadding: val })
							}
							resetAttributeKey="viewMorePadding"
						/>
						<Toggle
							checked={enableViewMoreBorder}
							onChange={() =>
								setAttributes({
									enableViewMoreBorder: !enableViewMoreBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enableViewMoreBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={viewMoreBorder}
											onChange={(v) => {
												setAttributes({
													viewMoreBorder: v,
												});
											}}
											resetAttributeKey="viewMoreBorder"
										/>
										<BoxShadow
											resetAttributeKey="viewMoreBoxShadow"
											value={viewMoreBoxShadow}
											onChange={(val) =>
												setAttributes({
													viewMoreBoxShadow: val,
												})
											}
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={viewMoreHoverBorder}
											onChange={(v) => {
												setAttributes({
													viewMoreHoverBorder: v,
												});
											}}
											resetAttributeKey="viewMoreHoverBorder"
										/>
										<BoxShadow
											resetAttributeKey="viewMoreBoxShadowHover"
											value={viewMoreBoxShadowHover}
											onChange={(val) =>
												setAttributes({
													viewMoreBoxShadowHover: val,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
						<Slider
							label={__("Icon Size", "magazine-blocks")}
							min={0}
							max={50}
							value={iconSize}
							onChange={(val: any) =>
								setAttributes({ iconSize: val })
							}
							responsive
							units={["px", "em", "%"]}
							resetAttributeKey="iconSize"
						/>
						<Typography
							value={viewAllTypography}
							onChange={(val) =>
								setAttributes({
									viewAllTypography: val,
								})
							}
							resetAttributeKey="viewAllTypography"
						/>
					</Panel>
				)}

				{enableDot && (
					<Panel title={__("Dots", "magazine-blocks")}>
						<Slider
							onChange={(val: any) =>
								setAttributes({ dotGap: val })
							}
							label={__("Gap", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={50}
							value={dotGap}
							resetAttributeKey="dotGap"
						/>
						<Slider
							onChange={(val: any) =>
								setAttributes({ dotHeight: val })
							}
							label={__("Height", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={50}
							value={dotHeight}
							resetAttributeKey="dotHeight"
						/>
						<Slider
							onChange={(val: any) =>
								setAttributes({ dotWidth: val })
							}
							label={__("Width", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={0}
							max={50}
							value={dotWidth}
							resetAttributeKey="dotWidth"
						/>
						<Slider
							onChange={(val: any) =>
								setAttributes({
									horizontalPosition: val,
								})
							}
							label={__("Horizontal Position", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={-1000}
							max={1000}
							value={horizontalPosition}
							resetAttributeKey="horizontalPosition"
						/>
						<Slider
							onChange={(val: any) =>
								setAttributes({ verticalPosition: val })
							}
							label={__("Vertical Position", "magazine-blocks")}
							units={["px", "em", "%"]}
							responsive={true}
							min={-1000}
							max={1000}
							value={verticalPosition}
							resetAttributeKey="verticalPosition"
						/>
						<PopoverDrawer
							label={__("Background", "magazine-blocks")}
						>
							<Tabs position="relative">
								<TabList>
									<Tab>{__("Normal", "magazine-blocks")}</Tab>
								</TabList>
								<TabPanels>
									<TabPanel>
										<Reset
											saved={dotBackground}
											resetKey="dotBackground"
											onReset={(v) => {
												setAttributes({
													dotBackground: v,
												});
											}}
											buttonProps={{
												position: "absolute",
												top: "12px",
												right: "10px",
											}}
										/>
										<Background
											value={dotBackground}
											onChange={(v) =>
												setAttributes({
													dotBackground: v,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						</PopoverDrawer>
						<Toggle
							checked={enableDotBorder}
							onChange={() =>
								setAttributes({
									enableDotBorder: !enableDotBorder,
								})
							}
							label={__("Border", "magazine-blocks")}
						/>
						{enableDotBorder && (
							<Tabs isFitted variant="unstyled">
								<TabList
									border="1px solid #E0E0E0"
									p="4px"
									borderRadius="2px"
								>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Normal
									</Tab>
									<Tab
										_selected={{
											color: "white",
											bg: "#7E36F4",
											borderRadius: "2px",
										}}
									>
										Hover
									</Tab>
								</TabList>
								<TabPanels>
									<TabPanel p="16px 0">
										<Border
											value={dotBorder}
											onChange={(v) => {
												setAttributes({
													dotBorder: v,
												});
											}}
											resetAttributeKey="dotBorder"
										/>
										<BoxShadow
											resetAttributeKey="dotBoxShadow"
											value={dotBoxShadow}
											onChange={(val) =>
												setAttributes({
													dotBoxShadow: val,
												})
											}
										/>
									</TabPanel>
									<TabPanel p="16px 0">
										<Border
											value={dotHoverBorder}
											onChange={(v) => {
												setAttributes({
													dotHoverBorder: v,
												});
											}}
											resetAttributeKey="dotHoverBorder"
										/>
										<BoxShadow
											resetAttributeKey="dotBoxShadowHover"
											value={dotBoxShadowHover}
											onChange={(val) =>
												setAttributes({
													dotBoxShadowHover: val,
												})
											}
										/>
									</TabPanel>
								</TabPanels>
							</Tabs>
						)}
					</Panel>
				)}
				<Panel title={__("Card Border", "magazine-blocks")}>
					{sliderStyle === "style3" ? (
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel paddingBottom="0">
									<Border
										value={cardBorder}
										onChange={(v) => {
											setAttributes({
												cardBorder: v,
											});
										}}
										resetAttributeKey="cardBorder"
									/>
									<BoxShadow
										resetAttributeKey="cardBoxShadow"
										value={cardBoxShadow}
										onChange={(val) =>
											setAttributes({
												cardBoxShadow: val,
											})
										}
									/>
								</TabPanel>
								<TabPanel paddingBottom="0">
									<Border
										value={cardBorderHover}
										onChange={(v) => {
											setAttributes({
												cardBorderHover: v,
											});
										}}
										resetAttributeKey="cardBorderHover"
									/>
									<BoxShadow
										resetAttributeKey="cardBoxShadowHover"
										value={cardBoxShadowHover}
										onChange={(val) =>
											setAttributes({
												cardBoxShadowHover: val,
											})
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					) : (
						<Dimensions
							value={radius || {}}
							responsive
							label={__("Border Radius", "magazine-blocks")}
							defaultUnit="px"
							units={["px", "rem", "em", "%"]}
							onChange={(val) => setAttributes({ radius: val })}
							resetAttributeKey="radius"
						/>
					)}
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
